<template>
  <div class="document-viewer">
    <div class="viewer-header">
      <div class="header-navigation">
        <button @click="goToProjectDocuments" class="nav-button">
          ← Project Documents
        </button>
        <button @click="goToProject" class="nav-button">
          → Project Dashboard  
        </button>
      </div>
      <h1>Document Source & Extraction View</h1>
      <div class="document-info">
        <span v-if="documentData">{{ documentData.filepath }}</span>
        <span v-if="documentData" class="status-badge" :class="documentData.status">{{ documentData.status }}</span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading document and extraction results...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="loadDocument" class="retry-button">Retry</button>
    </div>

    <div v-else-if="documentData" class="viewer-content">
      <!-- Document Preview with Overlay -->
      <div class="document-section">
        <h3>Source Document</h3>
        <div class="document-preview-container">
          <div class="document-preview" ref="documentPreview">
            <!-- Excel files - special handling -->
            <div v-if="isExcelDocument" class="excel-viewer">
              <!-- Loading state for Excel data -->
              <div v-if="isLoadingExcelData" class="excel-loading">
                <div class="spinner"></div>
                <p>Loading Excel data...</p>
              </div>
              
              <!-- Excel data display -->
              <div v-else-if="excelData && excelData.rows" class="excel-content">
                <div class="excel-header">
                  <div class="excel-info">
                    <h4>📊 {{ documentData?.filepath || 'Excel Document' }}</h4>
                    <p v-if="excelData.raw_excel_data">
                      Raw Excel File • {{ excelData.rows.length }} rows × {{ excelData.rows[0]?.length || 0 }} columns
                    </p>
                    <p v-else-if="excelData.fallback">
                      Excel file preview unavailable - use download button
                    </p>
                    <p v-else>{{ excelData.rows.length }} rows × {{ excelData.rows[0]?.length || 0 }} columns</p>
                  </div>
                  <div class="excel-actions">
                    <button @click="downloadDocument" class="btn-download">
                      📥 Download Excel File
                    </button>
                  </div>
                </div>

                <!-- Worksheet tabs -->
                <div v-if="excelData.available_sheets && excelData.available_sheets.length > 1" class="excel-tabs">
                  <div class="tabs-label">Worksheets:</div>
                  <div class="tabs-container">
                    <button 
                      v-for="sheet in excelData.available_sheets" 
                      :key="sheet"
                      @click="selectSheet(sheet)"
                      class="sheet-tab"
                      :class="{ 'active': sheet === selectedSheet }"
                    >
                      {{ sheet }}
                    </button>
                  </div>
                </div>
                
                <div class="excel-table-container">
                  <table class="excel-display-table">
                    <tbody>
                      <tr v-for="(row, rowIndex) in excelData.rows" :key="rowIndex" class="excel-row">
                        <td class="row-number">{{ rowIndex + 1 }}</td>
                        <td v-for="(cell, cellIndex) in row" :key="cellIndex" class="excel-cell">
                          {{ formatExcelCell(cell) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
            </div>
              </div>
              
              <!-- Fallback if Excel data fails to load -->
              <div v-else class="excel-fallback">
                <div class="document-icon">📊</div>
                <h4>{{ documentData?.filepath || 'Excel Document' }}</h4>
                <p class="doc-description">Unable to load Excel preview. Use the download button to view the spreadsheet.</p>
                <div class="viewer-actions">
                  <button @click="downloadDocument" class="btn-download primary">
                    📥 Download Excel File
                  </button>
                </div>
              </div>
            </div>
            <!-- Images and PDFs - try to show image first if we have blob URL -->
            <img 
              v-else-if="documentImageBlobUrl && !isExcelDocument" 
              :src="documentImageBlobUrl" 
              alt="Document preview"
              @load="onImageLoad"
              @error="onImageError"
              @click="openZoomModal"
              class="document-image clickable"
              title="Click to zoom and view full document"
            />
            <!-- Zoom icon overlay for images -->
            <div v-if="documentImageBlobUrl && !isExcelDocument" class="zoom-icon-overlay" @click="openZoomModal">
              <span class="zoom-icon">🔍</span>
              <span class="zoom-text">Click to zoom</span>
            </div>
            <!-- PDF documents - fallback viewer card if no blob URL but have image URL -->
            <div v-else-if="documentImageUrl && isPdfDocument && !isExcelDocument" class="document-viewer-card">
              <div class="viewer-placeholder">
                <div class="document-icon">📄</div>
                <h4>{{ documentData?.filepath || 'Document Preview' }}</h4>
                <p class="doc-info">Document ID: {{ documentId }}</p>
                <div class="viewer-actions">
                  <a :href="documentImageUrl" 
                     target="_blank" 
                     class="btn-view-doc">
                    🔍 View Document
                  </a>
                  <button @click="downloadDocument" class="btn-download">
                    📥 Download
                  </button>
                </div>
              </div>
            </div>
            <!-- Fallback if no document -->
            <div v-else-if="!isExcelDocument" class="no-document-placeholder">
              <div class="placeholder-content">
                <div class="document-icon">📄</div>
                <h4>Document Preview</h4>
                <p v-if="documentData?.filepath">{{ documentData.filepath }}</p>
                <p class="error-text">Unable to load document preview</p>
                <a v-if="documentImageUrl" :href="documentImageUrl" target="_blank" class="fallback-link">
                  🔗 Open Document Directly
                </a>
              </div>
            </div>
            <div class="bbox-overlay" ref="bboxOverlay"></div>
          </div>
        </div>
      </div>

      <!-- Extraction Results -->
      <div class="extraction-section">
        <h3>Extraction Results</h3>
        
        <!-- Add extraction quality warning for G702 documents -->
        <div v-if="isG702Document && (!keyValuePairs?.length && !tables?.length)" class="extraction-warning">
          <div class="warning-header">
            <span class="warning-icon">⚠️</span>
            <h4>Limited Extraction Results</h4>
          </div>
          <p>This appears to be a G702 document with limited structured extraction. This may be due to:</p>
          <ul>
            <li>Document quality or scanning issues</li>
            <li>Non-standard G702 format variations</li>
            <li>Parser configuration needs updating</li>
          </ul>
          <p>Raw OCR text is available below for manual review.</p>
        </div>

        <!-- Summary -->
        <div v-if="extractionSummary" class="extraction-summary">
          <div class="summary-item">
            <span class="label">Document Type:</span>
            <span class="value">{{ extractionSummary.document_type || 'Unknown' }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Confidence:</span>
            <span class="value">{{ Math.round((extractionSummary.confidence || 0) * 100) }}%</span>
          </div>
          <div class="summary-item">
            <span class="label">Processing Time:</span>
            <span class="value">{{ extractionSummary.processing_time || 'N/A' }}</span>
          </div>
        </div>

        <!-- Key-Value Pairs -->
        <div v-if="keyValuePairs && keyValuePairs.length" class="key-value-section">
          <h4>Extracted Key-Value Pairs</h4>
          <div class="key-value-list">
            <div 
              v-for="(item, index) in keyValuePairs" 
              :key="index"
              class="key-value-item"
              :class="{ 'low-confidence': item.confidence < 0.8 }"
              @mouseenter="highlightBbox(item.bbox)"
              @mouseleave="clearHighlight"
            >
              <div class="key-value-header">
                <span class="key">{{ item.key }}</span>
                <span class="confidence" :class="{ 'low': item.confidence < 0.8 }">
                  {{ Math.round(item.confidence * 100) }}%
                </span>
              </div>
              <div class="value">{{ item.value }}</div>
            </div>
          </div>
        </div>

        <!-- Tables -->
        <div v-if="tables && tables.length" class="tables-section">
          <h4>Extracted Tables</h4>
          <div v-for="(table, tableIndex) in tables" :key="tableIndex" class="table-container">
            <h5>Table {{ tableIndex + 1 }}</h5>
            <div class="table-wrapper" @mouseenter="highlightBbox(table.bbox)" @mouseleave="clearHighlight">
              <table class="extracted-table">
                <tbody>
                  <tr v-for="(row, rowIndex) in (table.rows || [])" :key="rowIndex">
                    <td 
                      v-for="(cell, cellIndex) in (Array.isArray(row) ? row : [])" 
                      :key="cellIndex"
                      class="table-cell"
                      :class="{ 'low-confidence': getTableCellConfidence(cell) < 0.8 }"
                      @mouseenter="highlightBbox(getTableCellBbox(cell))"
                      @mouseleave="clearHighlight"
                    >
                      {{ getTableCellText(cell) }}
                      <span class="cell-confidence">{{ Math.round(getTableCellConfidence(cell) * 100) }}%</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Raw OCR Text -->
        <div v-if="rawText" class="raw-text-section">
          <h4>Raw OCR Text</h4>
          <div class="raw-text-container">
            <pre class="raw-text">{{ rawText }}</pre>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="viewer-actions">
      <button @click="downloadResults" class="download-button">Download Results</button>
      <button @click="goBack" class="back-button">← Back to Project</button>
    </div>

    <!-- Zoom Modal -->
    <div v-if="showZoomModal" class="zoom-modal-backdrop" @click="closeZoomModal">
      <div class="zoom-modal" @click.stop>
        <div class="zoom-modal-header">
          <h3>Document Viewer</h3>
          <div class="zoom-controls">
            <button @click="zoomIn" class="zoom-btn">+</button>
            <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
            <button @click="zoomOut" class="zoom-btn">-</button>
            <button @click="resetZoom" class="zoom-btn">Reset</button>
          </div>
          <button @click="closeZoomModal" class="close-button">×</button>
        </div>
        <div class="zoom-modal-body" ref="zoomModalBody">
          <div class="zoom-image-container" 
               :style="{ transform: `scale(${zoomLevel})`, transformOrigin: transformOrigin }"
               @mousedown="startPan"
               @mousemove="pan"
               @mouseup="endPan"
               @wheel="handleWheel">
            <img :src="documentImageBlobUrl" alt="Document full view" class="zoom-image" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth_store';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const documentId = computed(() => route.params.id);
const documentData = ref(null);
const loading = ref(true);
const error = ref(null);
const documentImageUrl = ref(null);
const documentImageBlobUrl = ref(null); // For authenticated image loading
const extractionSummary = ref(null);
const keyValuePairs = ref([]);
const tables = ref([]);
const rawText = ref('');

// Add Excel data state
const excelData = ref(null);
const isLoadingExcelData = ref(false);
const selectedSheet = ref(null);

const documentPreview = ref(null);
const bboxOverlay = ref(null);

// Zoom modal functionality
const showZoomModal = ref(false);
const zoomLevel = ref(1);
const transformOrigin = ref('0 0');
const isPanning = ref(false);
const lastPanPoint = ref({ x: 0, y: 0 });
const zoomModalBody = ref(null);

// G702 document detection
const isG702Document = computed(() => {
  return documentData.value?.filepath?.toLowerCase().includes('g702') ||
         documentData.value?.document_type?.toLowerCase().includes('g702');
});

// PDF document detection
const isPdfDocument = computed(() => {
  return documentData.value?.filepath?.toLowerCase().includes('.pdf') ||
         documentData.value?.filepath?.toLowerCase().endsWith('pdf') ||
         documentData.value?.document_type?.toLowerCase().includes('pdf');
});

// Excel document detection
const isExcelDocument = computed(() => {
  return documentData.value?.filepath?.toLowerCase().includes('.xlsx') ||
         documentData.value?.filepath?.toLowerCase().includes('.xls') ||
         documentData.value?.filepath?.toLowerCase().endsWith('xlsx') ||
         documentData.value?.filepath?.toLowerCase().endsWith('xls');
});

// Fix API base URL - remove /api/v1 if it's already included
const rawApiBase = process.env.VUE_APP_API_URL || 'http://localhost:3000';
const apiBase = rawApiBase.replace('/api/v1', '');
const authToken = computed(() => ({
  headers: {
    'Authorization': `Bearer ${authStore.token}`,
    'Content-Type': 'application/json'
  }
}));

onMounted(() => {
  loadDocument();
});

onUnmounted(() => {
  // Clean up blob URL to prevent memory leaks
  if (documentImageBlobUrl.value) {
    URL.revokeObjectURL(documentImageBlobUrl.value);
  }
});

async function loadDocument() {
  try {
    loading.value = true;
    error.value = null;

    console.log('DocumentViewer - Loading document:', documentId.value);
    console.log('DocumentViewer - API Base:', apiBase);
    console.log('DocumentViewer - Auth Token:', authToken.value);

    // Load document details
    const docResponse = await axios.get(`${apiBase}/api/v1/documents/${documentId.value}`, authToken.value);
    console.log('DocumentViewer - Document response:', docResponse.data);
    documentData.value = docResponse.data;

    // Load document image
    if (documentData.value.filepath.includes('G703')) {
      documentImageUrl.value = `${apiBase}/api/v1/documents/${documentId.value}/image`;
    } else if (documentData.value.filepath.includes('G702')) {
      documentImageUrl.value = `${apiBase}/api/v1/documents/${documentId.value}/image`;
    } else {
      documentImageUrl.value = `${apiBase}/api/v1/documents/${documentId.value}/image`;
    }
    console.log('DocumentViewer - Image URL:', documentImageUrl.value);
    
    // Load the image with authentication (skip for Excel files)
    if (!isExcelDocument.value) {
      await loadAuthenticatedImage();
    } else {
      // For Excel files, load the Excel data instead
      await loadExcelData();
    }

    // Load extraction results if available - check multiple possible fields
    console.log('DocumentViewer - Full document data for extraction analysis:', documentData.value);
    
    let extractionData = null;
    let extractionSource = null;
    
    if (documentData.value.document_sections) {
      extractionData = documentData.value.document_sections;
      extractionSource = 'document_sections';
    } else if (documentData.value.sections) {
      extractionData = documentData.value.sections;
      extractionSource = 'sections';
    } else if (documentData.value.extraction_results) {
      extractionData = documentData.value.extraction_results;
      extractionSource = 'extraction_results';
    } else if (documentData.value.results) {
      extractionData = documentData.value.results;
      extractionSource = 'results';
    }
    
    if (extractionData) {
      console.log(`DocumentViewer - Found extraction data in '${extractionSource}', parsing...`);
      parseExtractionResults(extractionData);
    } else {
      console.log('DocumentViewer - No extraction results found in any expected fields', {
        status: documentData.value.status,
        availableFields: Object.keys(documentData.value),
        hasSections: !!documentData.value.document_sections,
        hasOcrText: !!documentData.value.ocr_text
      });
      
      // Still try to parse OCR text if available
      if (documentData.value.ocr_text) {
        console.log('DocumentViewer - Using available OCR text');
        rawText.value = documentData.value.ocr_text;
      }
    }

    loading.value = false;
  } catch (err) {
    console.error('DocumentViewer - Error loading document:', err);
    error.value = `Failed to load document: ${err.message}`;
    loading.value = false;
  }
}

async function loadAuthenticatedImage() {
  try {
    if (!documentImageUrl.value) return;
    
    console.log('DocumentViewer - Loading authenticated image:', documentImageUrl.value);
    
    const imageResponse = await fetch(documentImageUrl.value, {
      headers: authToken.value.headers
    });
    
    console.log('DocumentViewer - Image response status:', imageResponse.status);
    
    if (!imageResponse.ok) {
      console.error('DocumentViewer - Image fetch failed:', imageResponse.status, imageResponse.statusText);
      return;
    }
    
    // Convert response to blob
    const imageBlob = await imageResponse.blob();
    
    // Create blob URL
    if (documentImageBlobUrl.value) {
      URL.revokeObjectURL(documentImageBlobUrl.value); // Clean up previous blob URL
    }
    documentImageBlobUrl.value = URL.createObjectURL(imageBlob);
    
    console.log('DocumentViewer - Successfully created blob URL for authenticated image');
    console.log('DocumentViewer - Document type check:', {
      filepath: documentData.value?.filepath,
      document_type: documentData.value?.document_type,
      isPdfDocument: isPdfDocument.value,
      isExcelDocument: isExcelDocument.value,
      hasBlobUrl: !!documentImageBlobUrl.value
    });
    
  } catch (imageError) {
    console.error('DocumentViewer - Error loading authenticated image:', imageError);
  }
}

async function loadExcelData(sheet = null) {
  try {
    isLoadingExcelData.value = true;
    console.log('DocumentViewer - Loading Excel data for document:', documentId.value, 'sheet:', sheet);
    
    // 🔥 SIMPLE SOLUTION: Use standalone Excel preview controller (bypasses DocumentsController issues)
    let excelViewerUrl = `${apiBase}/api/v1/excel_preview/${documentId.value}`;
    if (sheet) {
      excelViewerUrl += `?sheet=${encodeURIComponent(sheet)}`;
    }
    console.log('DocumentViewer - Excel viewer URL:', excelViewerUrl);
    
    const response = await fetch(excelViewerUrl, {
      headers: authToken.value.headers
    });
    
    if (!response.ok) {
      throw new Error(`Failed to load Excel preview: ${response.status}`);
    }
    
    const excelResponseData = await response.json();
    console.log('DocumentViewer - Excel response:', excelResponseData);
    
    if (excelResponseData && excelResponseData.success && excelResponseData.data) {
      excelData.value = {
        rows: excelResponseData.data,
        totalItems: excelResponseData.data?.length || 0,
        filename: documentData.value?.filepath,
        current_sheet: excelResponseData.current_sheet,
        available_sheets: excelResponseData.available_sheets
      };
      
      // Set the selected sheet
      selectedSheet.value = excelResponseData.current_sheet;
      
      console.log('✅ DocumentViewer - Successfully loaded Excel data for sheet:', excelResponseData.current_sheet);
    } else {
      throw new Error('No Excel data returned');
    }
    
  } catch (err) {
    console.error('DocumentViewer - Error loading Excel data:', err);
    
    excelData.value = {
      rows: [
        ['Excel Preview Error'],
        [`Error: ${err.message}`],
        [`File: ${documentData.value?.filepath || 'Unknown'}`],
        ['Please try downloading the file directly']
      ],
      fallback: true
    };
  } finally {
    isLoadingExcelData.value = false;
  }
}

function selectSheet(sheet) {
  console.log('DocumentViewer - Selecting sheet:', sheet);
  if (sheet !== selectedSheet.value) {
    loadExcelData(sheet);
  }
}

function formatExcelCell(cell) {
  if (cell === null || cell === undefined) return '';
  if (typeof cell === 'object' && cell.value !== undefined) {
    return cell.value;
  }
  return String(cell);
}

function parseExtractionResults(sections) {
  if (!sections) return;

  console.log('DocumentViewer - Raw sections data:', sections);

  const kvPairs = [];
  const tableData = [];
  let extractedText = '';
  
  // Handle both array and object formats
  let sectionsArray = [];
  if (Array.isArray(sections)) {
    sectionsArray = sections;
  } else if (typeof sections === 'object') {
    // If it's an object, try to extract relevant data directly
    sectionsArray = [sections];
  }
  
  sectionsArray.forEach(section => {
    console.log('DocumentViewer - Processing section:', section);
    
    // Extract key-value pairs
    if (section.extracted_fields && Array.isArray(section.extracted_fields)) {
      section.extracted_fields.forEach(field => {
        kvPairs.push({
          key: field.field_name,
          value: field.extracted_value,
          confidence: field.confidence || 0,
          bbox: field.bbox
        });
      });
    }

    // Extract tables - handle both array and direct table object
    if (section.tables) {
      const tablesArray = Array.isArray(section.tables) ? section.tables : [section.tables];
      tablesArray.forEach(table => {
        if (table && table.rows) {
          tableData.push({
            bbox: table.bbox,
            rows: table.rows || []
          });
          console.log('DocumentViewer - Found table with', table.rows.length, 'rows');
        }
      });
    }

    // Extract raw text
    if (section.ocr_text) {
      extractedText += section.ocr_text + '\n';
    }
  });

  // If we still don't have OCR text but have document.ocr_text, use that
  if (!extractedText && documentData.value.ocr_text) {
    extractedText = documentData.value.ocr_text;
  }

  keyValuePairs.value = kvPairs;
  tables.value = tableData;
  rawText.value = extractedText;
  
  console.log('DocumentViewer - Parsed results:', {
    kvPairs: kvPairs.length,
    tables: tableData.length,
    textLength: extractedText.length
  });
  
  // Set summary
  extractionSummary.value = {
    document_type: documentData.value.document_type,
    confidence: documentData.value.total_confidence,
    processing_time: documentData.value.processing_metadata?.duration
  };
}

function highlightBbox(bbox) {
  if (!bbox || !bboxOverlay.value || !documentPreview.value) return;

  clearHighlight();

  const container = documentPreview.value;
  const mockInvoice = container.querySelector('.mock-invoice-image');
  const img = container.querySelector('.document-image');
  
  let rect;
  if (mockInvoice) {
    // Use mock invoice dimensions
    rect = mockInvoice.getBoundingClientRect();
  } else if (img) {
    // Use real image dimensions
    rect = img.getBoundingClientRect();
  } else {
    return;
  }

  // Create highlight element
  const highlight = document.createElement('div');
  highlight.className = 'bbox-highlight';
  
  // Calculate positions (bbox is normalized 0-1)
  const left = bbox[0] * rect.width;
  const top = bbox[1] * rect.height;
  const width = (bbox[2] - bbox[0]) * rect.width;
  const height = (bbox[3] - bbox[1]) * rect.height;

  highlight.style.left = `${left}px`;
  highlight.style.top = `${top}px`;
  highlight.style.width = `${width}px`;
  highlight.style.height = `${height}px`;

  bboxOverlay.value.appendChild(highlight);
}

function clearHighlight() {
  if (bboxOverlay.value) {
    bboxOverlay.value.innerHTML = '';
  }
}

function onImageLoad() {
  nextTick(() => {
    // Image loaded, can now properly calculate bbox positions
  });
}

function onImageError() {
  console.error('DocumentViewer - Failed to load document image');
  // Handle image error - could show fallback content
}

function onPdfLoad() {
  console.log('DocumentViewer - PDF loaded successfully');
  // PDF loaded successfully
}

async function downloadDocument() {
  try {
    console.log('DocumentViewer - Downloading document:', documentData.value?.filepath);
    
    if (isExcelDocument.value) {
      // For Excel files, we need to download the actual file, not the image endpoint
      const downloadUrl = `${apiBase}/api/v1/documents/${documentId.value}/download`;
      console.log('DocumentViewer - Excel download URL:', downloadUrl);
      
      // Use fetch to download with authentication
      const response = await fetch(downloadUrl, {
        headers: authToken.value.headers
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentData.value?.filepath || `document_${documentId.value}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      // For PDFs and other documents, use the image URL
      if (!documentImageUrl.value) return;
      
    const link = document.createElement('a');
    link.href = documentImageUrl.value;
    link.download = documentData.value?.filepath || `document_${documentId.value}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    }
  } catch (err) {
    console.error('DocumentViewer - Error downloading document:', err);
    alert('Failed to download document. Please try again.');
  }
}

async function downloadResults() {
  try {
    // eslint-disable-next-line vue/no-ref-as-operand
    const response = await axios.get(`${apiBase}/api/v1/documents/${documentId.value}/export`, {
      ...authToken.value,
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // eslint-disable-next-line vue/no-ref-as-operand
    link.download = `document_${documentId.value}_results.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error downloading results:', err);
  }
}

function goBack() {
  if (documentData.value && documentData.value.project_id) {
    router.push(`/projects/${documentData.value.project_id}`);
  } else {
    router.back();
  }
}

function goToProjectDocuments() {
  if (documentData.value && documentData.value.project_id) {
    router.push(`/projects/${documentData.value.project_id}/documents`);
  } else {
    router.back();
  }
}

function goToProject() {
  if (documentData.value && documentData.value.project_id) {
    router.push(`/portfolio/project-summary/${documentData.value.project_id}`);
  } else {
    router.push('/portfolio');
  }
}

function getTableCellText(cell) {
  if (typeof cell === 'object' && cell !== null && 'text' in cell) {
    return cell.text;
  } else if (typeof cell === 'string') {
    return cell;
  } else {
    return '';
  }
}

function getTableCellConfidence(cell) {
  if (typeof cell === 'object' && cell !== null && 'confidence' in cell) {
    return cell.confidence;
  } else {
    return 1.0;
  }
}

function getTableCellBbox(cell) {
  if (typeof cell === 'object' && cell !== null && 'bbox' in cell) {
    return cell.bbox;
  } else {
    return null;
  }
}

// Zoom modal functions
function openZoomModal() {
  showZoomModal.value = true;
  zoomLevel.value = 1;
  transformOrigin.value = '0 0';
}

function closeZoomModal() {
  showZoomModal.value = false;
  zoomLevel.value = 1;
  transformOrigin.value = '0 0';
  isPanning.value = false;
}

function zoomIn() {
  zoomLevel.value = Math.min(zoomLevel.value * 1.2, 5); // Max 5x zoom
}

function zoomOut() {
  zoomLevel.value = Math.max(zoomLevel.value / 1.2, 0.1); // Min 0.1x zoom
}

function resetZoom() {
  zoomLevel.value = 1;
  transformOrigin.value = '0 0';
}

function handleWheel(event) {
  event.preventDefault();
  const delta = event.deltaY;
  const rect = event.currentTarget.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  
  // Set transform origin to mouse position
  transformOrigin.value = `${x}px ${y}px`;
  
  if (delta < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

function startPan(event) {
  if (zoomLevel.value > 1) {
    isPanning.value = true;
    lastPanPoint.value = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  }
}

function pan(event) {
  if (isPanning.value && zoomLevel.value > 1) {
    const deltaX = event.clientX - lastPanPoint.value.x;
    const deltaY = event.clientY - lastPanPoint.value.y;
    
    // Update transform origin for panning effect
    const currentOrigin = transformOrigin.value.split(' ');
    const newX = parseFloat(currentOrigin[0]) - deltaX;
    const newY = parseFloat(currentOrigin[1]) - deltaY;
    
    transformOrigin.value = `${newX}px ${newY}px`;
    lastPanPoint.value = { x: event.clientX, y: event.clientY };
  }
}

function endPan() {
  isPanning.value = false;
}
</script>

<style scoped>
.document-viewer {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background: #f5f7fa;
}

.viewer-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.header-navigation {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.nav-button {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  text-align: center;
}

.viewer-header h1 {
  margin: 0 0 10px 0;
  font-size: 2.2em;
}

.document-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-badge {
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.8em;
}

.status-badge.done {
  background: #28a745;
}

.status-badge.failed {
  background: #dc3545;
}

.loading-state, .error-state {
  text-align: center;
  padding: 60px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.viewer-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.document-section {
  width: 100%;
}

.extraction-section {
  width: 100%;
}

.document-section h3 {
  margin: 0 0 20px 0;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 10px;
  font-size: 1.4em;
  font-weight: 600;
}

.extraction-section h3 {
  margin: 0 0 30px 0;
  color: #495057;
  border-bottom: 3px solid #667eea;
  padding-bottom: 15px;
  font-size: 1.4em;
  font-weight: 600;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.extraction-section h4 {
  color: #495057;
  font-size: 1.2em;
  font-weight: 600;
  margin: 30px 0 20px 0;
  padding: 10px 0;
  border-left: 4px solid #667eea;
  padding-left: 15px;
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, transparent 100%);
}

.tables-section {
  margin-bottom: 40px;
}

.key-value-section {
  margin-bottom: 40px;
}

.raw-text-section {
  margin-bottom: 20px;
}

.raw-text-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

.raw-text {
  padding: 20px;
  margin: 0;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  background: #fefefe;
  color: #495057;
}

.document-preview-container {
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.document-preview {
  position: relative;
  max-width: 100%;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.document-preview:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
  transform: translateY(-2px);
}

.document-image {
  width: 100%;
  max-width: 800px;
  height: auto;
  display: block;
  transition: opacity 0.3s ease;
}

.document-image.clickable {
  cursor: pointer;
}

.document-image.clickable:hover {
  opacity: 0.9;
}

.mock-invoice-image {
  width: 100%;
  min-height: 600px;
  background: white;
  border: 1px solid #ddd;
  padding: 40px;
  font-family: Arial, sans-serif;
  position: relative;
}

.invoice-header {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 30px;
  color: #333;
}

.invoice-number {
  position: absolute;
  top: 10%;
  left: 10%;
  font-size: 14px;
  color: #555;
}

.invoice-date {
  position: absolute;
  top: 20%;
  left: 10%;
  font-size: 14px;
  color: #555;
}

.invoice-table {
  position: absolute;
  top: 30%;
  left: 10%;
  width: 80%;
  border: 1px solid #ccc;
}

.table-header {
  display: flex;
  background: #f5f5f5;
  border-bottom: 1px solid #ccc;
  font-weight: bold;
}

.table-header span,
.table-row span {
  flex: 1;
  padding: 8px;
  border-right: 1px solid #ccc;
}

.table-header span:last-child,
.table-row span:last-child {
  border-right: none;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #ccc;
}

.invoice-total {
  position: absolute;
  top: 80%;
  right: 10%;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.bbox-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.bbox-highlight {
  position: absolute;
  background: rgba(255, 235, 59, 0.3);
  border: 2px solid #fbc02d;
  border-radius: 3px;
  pointer-events: none;
  transition: all 0.2s ease;
}

.extraction-summary {
  background: #e8f5e8;
  border: 2px solid #4caf50;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.summary-item .label {
  font-weight: 600;
  color: #2e7d32;
}

.summary-item .value {
  font-weight: 700;
  color: #1b5e20;
}

.key-value-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.key-value-item:hover {
  background: #e9ecef;
}

.key-value-item.low-confidence {
  border-color: #ffc107;
  background: #fff3cd;
}

.key-value-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.key {
  font-weight: 600;
  color: #495057;
}

.confidence {
  font-size: 0.9em;
  color: #28a745;
}

.confidence.low {
  color: #dc3545;
}

.value {
  font-size: 1.1em;
  color: #212529;
}

.table-container {
  margin-bottom: 30px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.table-container h5 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: 0;
  padding: 15px 20px;
  font-size: 1.1em;
  font-weight: 600;
}

.table-wrapper {
  overflow-x: auto;
  border: none;
  border-radius: 0;
}

.extracted-table {
  width: 100%;
  border-collapse: collapse;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  background: white;
}

.extracted-table tbody tr:nth-child(odd) {
  background-color: #f8f9fa;
}

.extracted-table tbody tr:nth-child(even) {
  background-color: white;
}

.extracted-table tbody tr:hover {
  background-color: #e3f2fd !important;
  transform: scale(1.001);
  transition: all 0.2s ease;
}

.table-cell {
  border: 1px solid #e9ecef;
  padding: 12px 8px;
  position: relative;
  cursor: pointer;
  vertical-align: middle;
  text-align: center;
  font-weight: 500;
  color: #495057;
  line-height: 1.4;
  min-width: 80px;
}

/* Header row styling */
.extracted-table tbody tr:first-child .table-cell {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 15px 8px;
  border-color: #1976d2;
}

/* First column (descriptions) - left align and wider */
.table-cell:first-child {
  text-align: left;
  font-weight: 600;
  min-width: 200px;
  padding-left: 15px;
  background-color: #f1f3f4;
}

.extracted-table tbody tr:first-child .table-cell:first-child {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  text-align: center;
}

/* Currency columns - right align */
.table-cell:nth-child(n+3) {
  text-align: right;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-weight: 600;
}

/* Percentage columns */
.table-cell:last-child {
  background-color: #e8f5e8;
  font-weight: 700;
  color: #2e7d32;
}

/* Amount formatting for currency cells */
.table-cell:nth-child(n+3):not(:last-child) {
  color: #1565c0;
}

/* Highlight important total rows */
.extracted-table tbody tr:last-child .table-cell {
  background-color: #fff3e0 !important;
  border-top: 2px solid #ff9800;
  font-weight: 700;
  color: #e65100;
}

.table-cell:hover {
  background: #bbdefb !important;
  border-color: #2196f3;
  transform: scale(1.02);
  z-index: 10;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.table-cell.low-confidence {
  background: #fff8e1 !important;
  border-color: #ffb74d;
  position: relative;
}

.table-cell.low-confidence::before {
  content: '⚠️';
  position: absolute;
  top: 2px;
  left: 4px;
  font-size: 10px;
}

.cell-confidence {
  font-size: 10px;
  color: #9e9e9e;
  position: absolute;
  top: 2px;
  right: 4px;
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 3px;
  border-radius: 2px;
  font-weight: 500;
}

/* High confidence - green */
.table-cell .cell-confidence {
  color: #4caf50;
}

/* Medium confidence - orange */
.table-cell.low-confidence .cell-confidence {
  color: #ff9800;
}

/* Make Trade Code column distinctive */
.table-cell:nth-child(1) {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border-right: 2px solid #ddd;
}

/* Make numbers more readable */
.table-cell:nth-child(n+3) {
  letter-spacing: 0.5px;
}

/* Zero values styling */
.table-cell:contains('$0') {
  color: #9e9e9e;
  font-style: italic;
}

.viewer-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.download-button, .back-button, .retry-button {
  padding: 12px 24px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.download-button {
  background: #28a745;
  color: white;
}

.download-button:hover {
  background: #218838;
}

.back-button {
  background: #6c757d;
  color: white;
}

.back-button:hover {
  background: #5a6268;
}

.retry-button {
  background: #007bff;
  color: white;
}

.retry-button:hover {
  background: #0056b3;
}

@media (max-width: 768px) {
  .viewer-content {
    grid-template-columns: 1fr;
  }
}

.zoom-icon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.document-preview:hover .zoom-icon-overlay {
  opacity: 1;
}

.zoom-icon {
  font-size: 48px;
  color: white;
  margin-bottom: 8px;
  animation: pulse 2s infinite;
}

.zoom-text {
  font-size: 16px;
  color: white;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.extraction-warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
}

.warning-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.warning-icon {
  font-size: 24px;
  margin-right: 10px;
}

.warning-header h4 {
  margin: 0;
  color: #856404;
  font-size: 18px;
}

.extraction-warning p {
  color: #856404;
  margin-bottom: 10px;
  line-height: 1.5;
}

.extraction-warning ul {
  color: #856404;
  margin-left: 20px;
  margin-bottom: 15px;
}

.extraction-warning li {
  margin-bottom: 5px;
}

.zoom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.zoom-modal {
  background: white;
  border-radius: 12px;
  width: 95vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.zoom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}

.zoom-modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.zoom-controls {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 5px;
}

.zoom-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  color: #495057;
  font-weight: 600;
}

.zoom-btn:hover {
  background-color: #e9ecef;
}

.zoom-level {
  font-size: 14px;
  margin: 0 15px;
  font-weight: 600;
  color: #495057;
  min-width: 50px;
  text-align: center;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #f0f0f0;
  color: #333;
}

.zoom-modal-body {
  flex: 1;
  overflow: auto;
  background: #f8f9fa;
  position: relative;
}

.zoom-image-container {
  position: relative;
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  transition: transform 0.2s ease;
}

.zoom-image-container:active {
  cursor: grabbing;
}

.zoom-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Document Viewer Card Styles */
.document-viewer-card {
  border: 1px solid #ddd;
  border-radius: 12px;
  overflow: hidden;
  background: #f9f9f9;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-placeholder {
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  margin: 20px;
  width: calc(100% - 40px);
}

.viewer-placeholder .document-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
  color: #6c757d;
}

.viewer-placeholder h4 {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.viewer-placeholder .doc-info {
  color: #666;
  font-size: 14px;
  margin-bottom: 25px;
}

.viewer-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.btn-view-doc, .btn-download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-view-doc {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.btn-view-doc:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
}

.btn-download {
  background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.btn-download:hover {
  background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

/* Enhanced Placeholder Styles */
.no-document-placeholder {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px dashed #dee2e6;
  border-radius: 12px;
}

.placeholder-content {
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
}

.placeholder-content .document-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.6;
}

.placeholder-content h4 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.placeholder-content p {
  margin: 0 0 10px 0;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.placeholder-content .error-text {
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 20px;
}

.fallback-link {
  display: inline-block;
  background: #007bff;
  color: white;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.fallback-link:hover {
  background: #0056b3;
  color: white;
}

/* Excel Document Viewer Styles */
.excel-viewer {
  background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
  border: 2px solid #c3e6cb;
}

.excel-viewer .viewer-placeholder {
  background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
  border: 1px solid #c3e6cb;
}

.excel-viewer .document-icon {
  color: #28a745 !important;
  font-size: 72px !important;
}

.excel-viewer h4 {
  color: #155724 !important;
  font-size: 20px !important;
}

.excel-viewer .doc-description {
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
  margin: 15px 0 20px 0;
  padding: 15px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  border-left: 4px solid #28a745;
}

.excel-viewer .btn-download.primary {
  background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
  font-size: 16px;
  padding: 14px 24px;
  box-shadow: 0 3px 10px rgba(40, 167, 69, 0.4);
}

.excel-viewer .btn-download.primary:hover {
  background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(40, 167, 69, 0.5);
}

/* Excel Content Display Styles */
.excel-content {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.excel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.excel-info h4 {
  margin: 0 0 5px 0;
  color: #155724;
  font-size: 18px;
  font-weight: 600;
}

.excel-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.excel-actions {
  display: flex;
  gap: 10px;
}

/* Excel Tabs Styles */
.excel-tabs {
  display: flex;
  align-items: center;
  padding: 10px 20px 0 20px;
  background: white;
  border-bottom: 1px solid #dee2e6;
}

.tabs-label {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  margin-right: 15px;
}

.tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  overflow-x: auto;
  flex: 1;
}

.sheet-tab {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  color: #495057;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
}

.sheet-tab:hover {
  background: #e9ecef;
  border-color: #ced4da;
  color: #212529;
}

.sheet-tab.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
  font-weight: 600;
}

.sheet-tab.active:hover {
  background: #0056b3;
  border-color: #0056b3;
}

.excel-table-container {
  flex: 1;
  overflow: auto;
  max-height: 600px;
  border: 1px solid #dee2e6;
}

.excel-display-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: white;
}

.excel-row:first-child {
  background-color: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.excel-row:first-child .excel-cell {
  background-color: #e9ecef;
  font-weight: 600;
  text-align: center;
  padding: 12px 8px;
}

.excel-row:hover:not(:first-child) {
  background-color: #f8f9fa;
}

.row-number {
  background-color: #f5f5f5;
  padding: 8px;
  border: 1px solid #ddd;
  font-weight: 600;
  width: 40px;
  text-align: center;
  position: sticky;
  left: 0;
  z-index: 2;
  font-size: 11px;
  color: #666;
}

.excel-cell {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: top;
  font-size: 12px;
  line-height: 1.4;
}

.excel-cell:hover {
  background-color: #e3f2fd;
  overflow: visible;
  white-space: normal;
  word-break: break-word;
}

.excel-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  background: white;
  border-radius: 8px;
}

.excel-loading .spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 15px;
}

.excel-loading p {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.excel-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  background: white;
  border-radius: 8px;
  text-align: center;
  padding: 40px 20px;
}

.excel-fallback .document-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.7;
  color: #28a745;
}

.excel-fallback h4 {
  margin: 0 0 15px 0;
  color: #155724;
  font-size: 18px;
  font-weight: 600;
}

.excel-fallback .doc-description {
  color: #666;
  font-size: 14px;
  margin-bottom: 25px;
  line-height: 1.5;
}
</style> 